import { Head, Link } from "@inertiajs/react"

import { WorkflowStatusBadge, deriveWorkflowStatus } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Activity {
  id: number
  title: string
}

interface TimeEntry {
  id: number
  entry_date: string | null
  user_name: string | null
  service_type: string | null
  time_type: string
  total_minutes: number
  mileage: number
  notes: string | null
  submitted: boolean
  approved: boolean
  paid: boolean
}

interface Props {
  activity: Activity
  time_entries: TimeEntry[]
  can_add: boolean
}

function formatMinutes(minutes: number): string {
  if (minutes <= 0) return "-"
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

export default function Index({ activity, time_entries, can_add }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Marketing Activities", href: "/marketing_activities" },
    { title: activity.title, href: `/marketing_activities/${activity.id}` },
    {
      title: "Time",
      href: `/marketing_activities/${activity.id}/time_entries`,
    },
  ]

  const statusBadge = (entry: TimeEntry) => (
    <WorkflowStatusBadge status={deriveWorkflowStatus(entry)} />
  )

  const totalMinutes = time_entries.reduce((sum, e) => sum + e.total_minutes, 0)
  const totalMileage = time_entries.reduce((sum, e) => sum + e.mileage, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Time - ${activity.title}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Time for {activity.title}</h1>
          {can_add && (
            <Link
              href={`/marketing_activities/${activity.id}/time_entries/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white"
            >
              Add Time Entry
            </Link>
          )}
        </div>

        {time_entries.length > 0 && (
          <div className="mb-4 rounded-lg bg-white p-4 shadow">
            <span className="text-hpi-med-gray text-sm">Total Time: </span>
            <span className="text-lg font-bold">
              {formatMinutes(totalMinutes)}
            </span>
            {totalMileage > 0 && (
              <>
                <span className="text-hpi-med-gray ml-4 text-sm">
                  Mileage:{" "}
                </span>
                <span className="font-bold">{totalMileage}</span>
              </>
            )}
            <span className="text-hpi-med-gray ml-4 text-sm">
              {time_entries.length}{" "}
              {time_entries.length === 1 ? "entry" : "entries"}
            </span>
          </div>
        )}

        {time_entries.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Employee
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Description
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Total
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Miles
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {time_entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/marketing_activities/${activity.id}/time_entries/${entry.id}`}
                      className="text-hpi-active-blue hover:underline"
                    >
                      {entry.entry_date}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/marketing_activities/${activity.id}/time_entries/${entry.id}`}
                      className="hover:underline"
                    >
                      {entry.service_type}
                    </Link>
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {entry.user_name}
                  </td>
                  <td className="border-hpi-light-gray max-w-xs truncate border px-4 py-2">
                    <span className="text-hpi-med-gray text-xs">
                      {entry.time_type}
                    </span>
                    {entry.notes && (
                      <div className="text-hpi-med-gray text-xs">
                        {entry.notes}
                      </div>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {formatMinutes(entry.total_minutes)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {entry.mileage > 0 ? entry.mileage : "-"}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {statusBadge(entry)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="rounded-lg bg-white p-6 text-center shadow">
            <p className="text-hpi-med-gray mb-4">
              No time entries found for this event.
            </p>
            {can_add && (
              <Link
                href={`/marketing_activities/${activity.id}/time_entries/new`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white"
              >
                Add Time Entry
              </Link>
            )}
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/marketing_activities/${activity.id}`}
            className="text-hpi-active-blue hover:underline"
          >
            Back to Event
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
